import { Skeleton } from '@mantine/core';
import { IconClipboardList } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDay } from '../../format.js';
import { followLink, useShell } from '../../shell-context.js';
import { RecordBadges } from './FormRecordParts.js';
import classes from './Forms.module.css';
import { EMPTY_HISTORY_FILTER, historySearch } from './history.js';
import { FORM_HISTORY_PATH, recordPath } from './record-model.js';
import type { FormRecordSource } from './source.js';
import { recordStatusBadges } from './status.js';
import type { FormRecordPage } from './types.js';

/** History sekmesinde gösterilen son kayıt sayısı; fazlası Form History'de. */
export const DOCUMENT_RECORDS_LIMIT = 10;

type State = { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; page: FormRecordPage };

/**
 * Form List ▸ History sekmesi: revizyon ağacının altında o formun kayıtları (gereksinim §9;
 * ADR 0042 §21): en yeni kayıtlar durum rozetleriyle; tamamı Form History'de form süzgeciyle.
 */
export function DocumentRecords({
  source,
  documentId,
  initial,
}: {
  source: FormRecordSource;
  documentId: string;
  /** Önceden okunmuş liste (birim testi). */
  initial?: FormRecordPage;
}) {
  const { t } = useTranslation();
  const shell = useShell();
  const [state, setState] = useState<State>(
    initial ? { kind: 'ready', page: initial } : { kind: 'loading' },
  );
  const preloaded = initial !== undefined;

  useEffect(() => {
    if (preloaded) return;
    let live = true;
    setState({ kind: 'loading' });
    const load = () =>
      source
        .list({
          subject: `document:${documentId}`,
          sort: '-recordNo',
          page: 1,
          pageSize: DOCUMENT_RECORDS_LIMIT,
        })
        .then(
          (page) => live && setState({ kind: 'ready', page }),
          () => live && setState({ kind: 'error' }),
        );
    void load();
    const stop = source.subscribe?.(() => void load());
    return () => {
      live = false;
      stop?.();
    };
  }, [preloaded, source, documentId]);

  const all = `${FORM_HISTORY_PATH}${historySearch({ ...EMPTY_HISTORY_FILTER, form: documentId })}`;
  return (
    <section className={classes.documentRecords} aria-labelledby={`records-${documentId}`}>
      <h3 id={`records-${documentId}`} className={classes.subheading}>
        <IconClipboardList size={16} aria-hidden /> {t('forms.history.ofForm')}
      </h3>
      {state.kind === 'loading' ? (
        <div aria-busy="true">
          <span className={classes.srOnly}>{t('forms.history.loading')}</span>
          <Skeleton height={14} width="70%" />
          <Skeleton height={14} width="50%" mt={8} />
        </div>
      ) : state.kind === 'error' ? (
        <p className={classes.muted} role="alert">
          {t('forms.history.loadError')}
        </p>
      ) : state.page.total === 0 ? (
        <p className={classes.muted}>{t('forms.history.noneForForm')}</p>
      ) : (
        <>
          <ul className={classes.documentRecordList}>
            {state.page.items.map((item) => {
              const path = recordPath(item.id);
              return (
                <li key={item.id} className={classes.documentRecord}>
                  <a
                    href={path}
                    className={classes.recordLink}
                    onClick={(event) => shell && followLink(event, path, shell.navigate)}
                  >
                    {item.recordNo}
                  </a>
                  <span className={classes.muted}>{formatDay(item.recordDay)}</span>
                  <span className={classes.documentRecordTitle} title={item.title ?? ''}>
                    {item.title ?? ''}
                  </span>
                  <RecordBadges badges={recordStatusBadges(t, item.status, item.flags)} />
                </li>
              );
            })}
          </ul>
          <a
            href={all}
            className={classes.allRecords}
            onClick={(event) => shell && followLink(event, all, shell.navigate)}
          >
            {t('forms.history.viewAll', { count: state.page.total })}
          </a>
        </>
      )}
    </section>
  );
}
